import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Admin.css';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');
const ADMIN_EMAIL = 'benjamin@universallythinking.com';

const Admin = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userBooks, setUserBooks] = useState([]);
  const [userSummaries, setUserSummaries] = useState([]);
  const [userStudyGuides, setUserStudyGuides] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState('');

  // Check if user is admin and on localhost
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }

    // Check if running on localhost
    const isLocalhost = 
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '';

    if (!isLocalhost) {
      navigate('/');
      return;
    }

    if (!user) {
      navigate('/login');
      return;
    }

    if (user.email?.toLowerCase() !== ADMIN_EMAIL.toLowerCase()) {
      navigate('/');
      return;
    }

    fetchData();
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/admin-stats/stats`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API_URL}/admin-stats/users`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data.users);
    } catch (err) {
      console.error('Error fetching admin data:', err);
      setError(err.response?.data?.error || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      setSelectedUser(users.find(u => u.id === userId));
      setActiveTab('user-details');

      const [booksRes, summariesRes, guidesRes] = await Promise.all([
        axios.get(`${API_URL}/admin-stats/users/${userId}/books`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API_URL}/admin-stats/users/${userId}/summaries`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API_URL}/admin-stats/users/${userId}/study-guides`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      setUserBooks(booksRes.data.books);
      setUserSummaries(summariesRes.data.summaries);
      setUserStudyGuides(guidesRes.data.studyGuides);
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError(err.response?.data?.error || 'Failed to load user details');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading">Loading admin data...</div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="admin-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <button onClick={fetchData} className="btn btn-secondary">Refresh</button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="admin-tabs">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={activeTab === 'users' ? 'active' : ''}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        {selectedUser && (
          <button
            className={activeTab === 'user-details' ? 'active' : ''}
            onClick={() => setActiveTab('user-details')}
          >
            User Details
          </button>
        )}
      </div>

      {activeTab === 'overview' && stats && (
        <div className="admin-overview">
          <h2>Overall Statistics</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">{stats.total_users}</div>
              <div className="stat-label">Total Users</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.premium_users}</div>
              <div className="stat-label">Premium Users</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.total_books}</div>
              <div className="stat-label">Total Books</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.total_saved_books}</div>
              <div className="stat-label">Saved Books</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.total_summaries}</div>
              <div className="stat-label">Summaries</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.total_study_guides}</div>
              <div className="stat-label">Study Guides</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.total_summary_requests}</div>
              <div className="stat-label">Summary Requests</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.total_ratings}</div>
              <div className="stat-label">Ratings</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.average_rating || 'N/A'}</div>
              <div className="stat-label">Avg Rating</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.recent_signups_7d}</div>
              <div className="stat-label">Signups (7d)</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.recent_upgrades_7d}</div>
              <div className="stat-label">Upgrades (7d)</div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="admin-users">
          <h2>All Users ({users.length})</h2>
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Premium</th>
                  <th>Premium Expires</th>
                  <th>Saved Books</th>
                  <th>Summaries</th>
                  <th>Study Guides</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.id}</td>
                    <td>{u.email}</td>
                    <td>{u.name || 'N/A'}</td>
                    <td>{u.is_premium ? '⭐ Yes' : 'No'}</td>
                    <td>{u.premium_expires_at ? formatDate(u.premium_expires_at) : 'N/A'}</td>
                    <td>{u.saved_books_count}</td>
                    <td>{u.summaries_count}</td>
                    <td>{u.study_guides_count}</td>
                    <td>{formatDate(u.created_at)}</td>
                    <td>
                      <button
                        onClick={() => fetchUserDetails(u.id)}
                        className="btn btn-small"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'user-details' && selectedUser && (
        <div className="admin-user-details">
          <div className="user-details-header">
            <h2>User Details: {selectedUser.email}</h2>
            <button onClick={() => setActiveTab('users')} className="btn btn-secondary">
              Back to Users
            </button>
          </div>

          <div className="user-info">
            <div className="info-item">
              <strong>Name:</strong> {selectedUser.name || 'N/A'}
            </div>
            <div className="info-item">
              <strong>Premium:</strong> {selectedUser.is_premium ? '⭐ Yes' : 'No'}
            </div>
            <div className="info-item">
              <strong>Premium Expires:</strong> {selectedUser.premium_expires_at ? formatDate(selectedUser.premium_expires_at) : 'N/A'}
            </div>
            <div className="info-item">
              <strong>Joined:</strong> {formatDate(selectedUser.created_at)}
            </div>
          </div>

          <div className="user-sections">
            <div className="user-section">
              <h3>Saved Books ({userBooks.length})</h3>
              <div className="books-list">
                {userBooks.length > 0 ? (
                  userBooks.map((book) => (
                    <div key={book.id} className="book-item">
                      {book.image_url && (
                        <img src={book.image_url} alt={book.title} className="book-thumbnail" />
                      )}
                      <div className="book-info">
                        <div className="book-title">{book.title}</div>
                        <div className="book-authors">{book.authors?.join(', ') || 'Unknown Author'}</div>
                        <div className="book-date">Saved: {formatDate(book.saved_at)}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No saved books</p>
                )}
              </div>
            </div>

            <div className="user-section">
              <h3>Summaries ({userSummaries.length})</h3>
              <div className="summaries-list">
                {userSummaries.length > 0 ? (
                  userSummaries.map((summary) => (
                    <div key={summary.id} className="summary-item">
                      <div className="summary-header">
                        <h4>{summary.book_title}</h4>
                        <span className="summary-date">{formatDate(summary.created_at)}</span>
                      </div>
                      <div className="summary-text">
                        {summary.summary_text.substring(0, 200)}...
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No summaries</p>
                )}
              </div>
            </div>

            <div className="user-section">
              <h3>Study Guides ({userStudyGuides.length})</h3>
              <div className="guides-list">
                {userStudyGuides.length > 0 ? (
                  userStudyGuides.map((guide) => (
                    <div key={guide.id} className="guide-item">
                      <div className="guide-header">
                        <h4>{guide.book_title}</h4>
                        <span className="guide-date">{formatDate(guide.created_at)}</span>
                      </div>
                      <div className="guide-text">
                        {guide.guide_text.substring(0, 200)}...
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No study guides</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
